import { useMemo, useCallback } from 'react'
import { useAppAuth } from '@/contexts/app-auth.context'

/**
 * Hook to check if a feature flag is enabled
 *
 * Feature flags come from the centralized user context (fetched at session start)
 * This hook now uses feature flags from AppAuthProvider instead of fetching separately
 *
 * @example
 * ```tsx
 * const { isEnabled } = useFeatureFlag()
 *
 * if (isEnabled('reporting')) {
 *   return <ReportingPage />
 * }
 * return <div>Feature not available</div>
 * ```
 *
 * @example
 * ```tsx
 * // Conditional rendering
 * const { isEnabled } = useFeatureFlag()
 *
 * return (
 *   <div>
 *     {isEnabled('show-beta-feature') && <BetaFeature />}
 *   </div>
 * )
 * ```
 */
export function useFeatureFlag() {
  const { featureFlags, isCompanyLoading, isInitialized } = useAppAuth()

  const flags = useMemo<Set<string>>(() => {
    const flagSet = new Set<string>()
    const flagsList = featureFlags ?? []
    for (const flag of flagsList) {
      flagSet.add(flag)
    }
    return flagSet
  }, [featureFlags])

  const isEnabled = useCallback(
    (flag: string): boolean => {
      return flags.has(flag)
    },
    [flags],
  )

  const getAllFlags = useCallback((): string[] => {
    return Array.from(flags)
  }, [flags])

  // Loading is true if not initialized yet OR if currently loading
  // This prevents showing "Feature Not Available" before flags are loaded
  const isLoading = !isInitialized || isCompanyLoading

  return {
    isEnabled,
    getAllFlags,
    flags,
    isLoading,
  }
}
